// arbeitsansicht.ts — DOM-freie Logik der „Arbeitsansicht" der Kontaktliste
// (User 28.09.2026): einklappbare Abschnitte „Hat heute Geburtstag", „Neue
// Mail", „Verpasster Anruf" und „Alle Kontakte" (offene Punkte zuerst, dann A–Z). Die Daten kommen
// aus der gewöhnlichen Kontaktliste (`apps/api/src/contactArbeitsansicht.ts`).

export type AbschnittKey = "gb" | "mail" | "anruf" | "all";
export type AbschnitteOffen = Record<AbschnittKey, boolean>;

export const ABSCHNITTE_STANDARD: AbschnitteOffen = { gb: true, mail: true, anruf: true, all: true };

const SPEICHER_ANSICHT = "kmvs.kontakte.arbeitsansicht";
const SPEICHER_ABSCHNITTE = "kmvs.kontakte.arbeitsansicht.abschnitte";

/** Arbeitsansicht an/aus — je Browser gemerkt. */
export function arbeitsansichtLesen(): boolean {
  try {
    return localStorage.getItem(SPEICHER_ANSICHT) === "1";
  } catch {
    return false;
  }
}
export function arbeitsansichtSpeichern(an: boolean): void {
  try {
    localStorage.setItem(SPEICHER_ANSICHT, an ? "1" : "0");
  } catch {
    /* privates Fenster o. Ä. — dann eben nicht gemerkt */
  }
}

export function abschnitteLesen(): AbschnitteOffen {
  try {
    const roh = JSON.parse(localStorage.getItem(SPEICHER_ABSCHNITTE) ?? "null");
    if (roh && typeof roh === "object") {
      return {
        gb: roh.gb !== false,
        mail: roh.mail !== false,
        anruf: roh.anruf !== false,
        all: roh.all !== false,
      };
    }
  } catch {
    /* Standard */
  }
  return { ...ABSCHNITTE_STANDARD };
}
export function abschnitteSpeichern(offen: AbschnitteOffen): void {
  try {
    localStorage.setItem(SPEICHER_ABSCHNITTE, JSON.stringify(offen));
  } catch {
    /* s. o. */
  }
}

/** Hat die Zeile einen offenen Punkt (Vorgang/NKG, Termin, Schaden, Zahlungsverkehr)? */
export function hatOffenePunkte(r: { openEventTypes?: string[] | null; nkgVorgangId?: number | null }): boolean {
  return (r.openEventTypes?.length ?? 0) > 0 || !!r.nkgVorgangId;
}

/** Das Alter, das jemand mit diesem Geburtsdatum am Tag `heute` erreicht. */
export function alterAm(geburt: string | undefined | null, heute: Date): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(geburt ?? "");
  if (!m) return null;
  const alter = heute.getFullYear() - Number(m[1]);
  return alter >= 0 && alter < 150 ? alter : null;
}

const zweistellig = (n: number) => String(n).padStart(2, "0");

/** „09:14" für heute, „Gestern" für gestern, sonst „27.09.". */
export function mailZeitLabel(iso: string, jetzt: Date): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const tag = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diffTage = Math.round((tag(jetzt) - tag(d)) / 86_400_000);
  if (diffTage === 0) return `${zweistellig(d.getHours())}:${zweistellig(d.getMinutes())}`;
  if (diffTage === 1) return "Gestern";
  return `${zweistellig(d.getDate())}.${zweistellig(d.getMonth() + 1)}.`;
}

/** Neueste Mail zuerst. */
export function nachMailZeit<T extends { neueMail?: { zeit: string } | null }>(zeilen: T[]): T[] {
  return [...zeilen].sort((a, b) => (b.neueMail?.zeit ?? "").localeCompare(a.neueMail?.zeit ?? ""));
}

/**
 * Montag 00:00 dieser Woche (Ortszeit des Browsers — die Nutzer sitzen in
 * Deutschland). Gleiches Fenster wie der Abschnitt „Verpasster Anruf" im
 * Backend (`wochenbeginnBerlin`).
 */
export function wochenbeginn(jetzt: Date): Date {
  const montag = new Date(jetzt.getFullYear(), jetzt.getMonth(), jetzt.getDate());
  montag.setDate(montag.getDate() - ((montag.getDay() + 6) % 7));
  return montag;
}

const WOCHENTAG = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];

/** „09:14" für heute, sonst „Mo 09:14" — der Abschnitt zeigt nur diese Woche. */
export function anrufZeitLabel(iso: string, jetzt: Date): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const uhr = `${zweistellig(d.getHours())}:${zweistellig(d.getMinutes())}`;
  const heute = d.getFullYear() === jetzt.getFullYear() && d.getMonth() === jetzt.getMonth() && d.getDate() === jetzt.getDate();
  return heute ? uhr : `${WOCHENTAG[d.getDay()]} ${uhr}`;
}

/** Neuester verpasster Anruf zuerst. */
export function nachAnrufZeit<T extends { verpassterAnruf?: { zeit: string } | null }>(zeilen: T[]): T[] {
  return [...zeilen].sort((a, b) => (b.verpassterAnruf?.zeit ?? "").localeCompare(a.verpassterAnruf?.zeit ?? ""));
}
